/* eslint-disable react-hooks/rules-of-hooks */
/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

/* eslint-disable no-useless-computed-key */
/* eslint-disable no-unused-expressions */

import React, {useContext, useEffect, useState} from 'react';

import FormContainer from "components/FormContainer";
import {FormSectionGlobal as FormSection} from "@fitinsur/components";
import {Button} from 'components/index';
import {ButtonContainer} from 'components/Button/index';
import {GlobalAuthContext, GlobalLoaderContext} from 'store/context';
import {useImportStatus} from 'services/QuoteService';
import {FitinsurAPI, MessageCenter} from 'utils';
import {TagsList} from '@fitinsur/components';
import ModalPremiumCalculation from 'components/ModalPremiumCalculation/index';
import {useSchedulleConfirm, useSchedulleExpire} from 'services/QuoteService';
import HasPermission from 'components/HasPermission/index';
import PropTypes from 'prop-types';

function PremiumCalculationSection({
  disableAll = false,
  formikRef,
  questionnaire,
  setQuestionnaire,
  values,
  errors,
  setFieldValue,
  setFieldError,
  idOperation,
  idOperationParent,
  path,
  onRemove = null,
  permissions,
  useGetQuestionnaireAnswerRequest = null,
  useSaveQuestionnaireAnswerRequest = null,
  currentPeriod = null,
  questionsIdsToSave = [1977],
  hideExportButton = false
}) {

  const {
    loading: useImportStatusLoading,
    request: useImportStatusRequest,
  } = useImportStatus(idOperation);

  const {
    loading: useSchedulleConfirmLoading,
    request: useSchedulleConfirmRequest,
  } = useSchedulleConfirm();

  const {
    loading: useSchedulleExpireLoading,
    request: useSchedulleExpireRequest,
  } = useSchedulleExpire();

  const loading = useContext(GlobalLoaderContext);
  const authContext = useContext(GlobalAuthContext);
  const fitinsur = new FitinsurAPI();
  const mc = new MessageCenter();

  const [schedulleDoc, setSchedulleDoc] = useState(null);
  const [isUpdatedSchedule] = useState(path?.includes('market-composition-updated-schedule'));
  const [premiumCalculation, setPremiumCalculation] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (useImportStatusLoading || useSchedulleExpireLoading || useSchedulleConfirmLoading) {
      loading.start();
    } else {
      loading.stop();
    }
  }, [useImportStatusLoading, useSchedulleExpireLoading, useSchedulleConfirmLoading]);

  useEffect(() => {
    const premiumAttachmentQst = questionnaire.find((qst) => qst?.idQuestion === 1977);

    if (premiumAttachmentQst?.answer.length && Array.isArray(premiumAttachmentQst?.answer)) {
      setPremiumCalculation(premiumAttachmentQst?.answer);
    }
  }, [questionnaire]);

  useEffect(() => {
    if (schedulleDoc) {
      handleImport();
      setPremiumCalculation([schedulleDoc]);
    }
  }, [schedulleDoc]);

  useEffect(() => {
    if (retry > 0 && retry < 21) {
      useImportStatusRequest().then(({data: res}) => {
        if (res.ret_code !== 200) {
          mc.error(res.ret_msg_details, true);
          setRetry(0);
          return;
        }

        if (res.ret_value.idDomainStatus) {
          if (res.ret_value.idDomainStatus === 35) {
            setShowModal(true);
            setRetry(0);
          } else if (res.ret_value.idDomainStatus === 36) {
            if (res.ret_value.errorsImportSchedulle) {
              res.ret_value.errorsImportSchedulle.forEach((i) => {
                if (i.ret_msg_details) {
                  mc.error(i.ret_msg_details);
                }
              });
            }
            setRetry(0);
          } else {
            setTimeout(() => setRetry(retry + 1), 750);
          }
        }
      }).catch(() => {
        setRetry(0);
      });
    } else if (retry === 21) {
      mc.error('Import Timeout');
      setRetry(0);
    } else if (retry === 0) {
    }
  }, [retry]);

  const handleImport = () => {
    const formData = new FormData();
    formData.append('idDocument', schedulleDoc.documentId);
    formData.append('idOperation', idOperation);
    formData.append('isUpdatedSchedule', isUpdatedSchedule);

    loading.start();

    fitinsur.setAuthToken(authContext.token);
    fitinsur.requestImportExcel(formData).then(x => x.json()).then(res => {
      if (res.ret_code != 200) {
        mc.error(res.ret_msg_details);
        questionnaire.map((qst) => {
          if (qst.idQuestion === 1977) {
            qst.answer = [];
            qst.answerJson = [];
            setFieldValue(qst.key, [])
          }

          return qst;
        });
        removePremiumCalculation();
        return;
      }
      setRetry(1);

    }).catch(err => {
      mc.error("An error happened, please try again");
      removePremiumCalculation();
    });
  };

  function testJSON(text) {
    if (typeof text !== "string") {
      return false;
    }
    try {
      JSON.parse(text);
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleExport = async () => {
    loading.start();

    let name;
    fitinsur.setAuthToken(authContext.token);
    fitinsur.generateExceXLS({idOperation: idOperation})
      .then(x => {
        if (x.status === 200) {
          name = x.headers.get('Content-Disposition');
          return x.blob();

        } else {
          return x.text();
        }
      }).then(res => {

      try {
        if (testJSON(res)) {
          res = JSON.parse(res);
          mc.error(res.ret_msg_details);
          loading.stop();
          return;
        }
        const url = window.URL.createObjectURL(res);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;

        name = (name.split('='))[1].replace('"', '');
        a.download = name.substring(0, name.length - 1);
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        loading.stop();
      } catch (e) {
        mc.error("An error happened, please try again");
        loading.stop();
        return;
      }
    });
  };

  const removePremiumCalculation = (index) => {
    setSchedulleDoc(null);
    setPremiumCalculation([]);
    handleSaveQuestionnaire(true);

    if (onRemove) {
      onRemove();
    }
  };

  function handleSaveQuestionnaire(isDeleted = false) {
    const cloneQuestionnaire = JSON.parse(JSON.stringify(questionnaire));

    let validQuestionnaire = cloneQuestionnaire.filter((question) => questionsIdsToSave.includes(question.idQuestion));

    validQuestionnaire = validQuestionnaire.map((question) => {
      if (question.idQuestion === 1977) {
        question.answer = !isDeleted ? [schedulleDoc] : [];
      }

      return question;
    });

    validQuestionnaire = validQuestionnaire.map((question) => {
      if ((question.idDomainQuestion === 270
        || question.idDomainQuestion === 271
        || (question.idDomainQuestion === 44 && typeof question.answer === 'object')
      ) && question.answer) {
        try {
          question.answer = JSON.stringify(question.answer)
        } catch (e) {
        }
      }

      return question;
    });

    useSaveQuestionnaireAnswerRequest(validQuestionnaire)
      .then((resp) => {
        setPremiumCalculation(!isDeleted ? [schedulleDoc] : []);
        setShowModal(false);

        if (useGetQuestionnaireAnswerRequest) {
          useGetQuestionnaireAnswerRequest()
        }

        setShowModal(false);
      })
      .catch((e) => {
        setPremiumCalculation([]);
        setShowModal(false);
        mc.error("An error happened, please try again");
      })
  };

  const handleConfirm = () => {
    loading.start();
    useSchedulleConfirmRequest({idOperation}, null).then(({data: res}) => {
      if (res.ret_code != 200) mc.error(res.ret_msg_details);
      handleSaveQuestionnaire();
    })
      .catch((e) => {
        removePremiumCalculation();
        mc.error("An error happened, please try again");
        loading.stop()
      });
  };

  return (
    <FormContainer title={"PREMIUM CALCULATIONS"} show={true}>
      {showModal && (
        <ModalPremiumCalculation
          show={showModal}
          idOperation={idOperation}
          handleConfirm={handleConfirm}
          handleCancel={removePremiumCalculation}
          currentPeriod={currentPeriod}
        />
      )}

      {premiumCalculation?.length ? (
        <TagsList
          disabled={disableAll}
          tags={premiumCalculation}
          disableType
          removeTag={removePremiumCalculation}
        />
      ) : (
        <FormSection
          disableAll={disableAll}
          sectionName="PREMIUM_CALCULATION"
          formikRef={formikRef}
          questionnaire={questionnaire}
          setQuestionnaire={setQuestionnaire}
          values={{
            ...values,
            documentTypeRequired: false,
            notShowColumns: true,
            showDragZoneText: true,
            translateText: true,
            autoUpload: false,
            ignoreAttachmentsDisplay: true
          }}
          error={errors}
          setFieldValue={setFieldValue}
          setFieldError={setFieldError}
          idOperation={idOperation}
          idOperationParent={idOperationParent}
          onQuestionChange={(question, answer, filtered, setFiltered) => {
            if (answer.length) {
              setSchedulleDoc(answer[0]);
            }
          }}
        />
      )}

      <ButtonContainer>
        <label>Note: Premium calculations WILL NOT be signed or scratched NOR sent to client via Connexion</label>
        <HasPermission
          permission={permissions.EXPORT_SCHEDULE && !hideExportButton}
        >
          <Button
            disabled={disableAll}
            primary
            onClick={() => {
              handleExport();
            }}
            style={{marginLeft: "10px", textAlign: "center", marginTop: "5px", alignSelf: 'flexEnd'}}
          >
            EXPORT
          </Button>
        </HasPermission>
      </ButtonContainer>
    </FormContainer>
  );
}

PremiumCalculationSection.propTypes = {
  onRemove: PropTypes.func,
};

PremiumCalculationSection.defaultProps = {
  onRemove: null,
};

export default PremiumCalculationSection;